/**
 * Builds a letter (<legal-letter>, LegalLetter.ts) from its LD template and the
 * values typed into the form. Pure functions, no DOM - the same result feeds
 * the preview, the print copy and the plain text for the clipboard.
 *
 * Templates come from LD and use single-brace placeholders (`{fullName}`), so
 * i18next's own `{{…}}` interpolation leaves them alone. A line, paragraph or
 * attachment with `when` appears only if that field is filled in (checkbox:
 * ticked); with `unless` only if it isn't. A placeholder for an empty field
 * becomes a dotted gap, so an incomplete letter can still be printed and
 * completed by hand.
 */

export type LegalLetterFieldType = 'text' | 'textarea' | 'date' | 'checkbox';

export interface LegalLetterField {
    name: string;
    label: string;
    type: LegalLetterFieldType;
    required?: boolean;
    hint?: string;
    placeholder?: string;
    autocomplete?: string;
    /** Initial value; `today` for a date field means the current day. */
    default?: string;
}

/** A text line, optionally shown only when a field is (or isn't) filled in. */
export type LegalLetterLine = string | { text: string; when?: string; unless?: string };

export type LegalLetterBlock =
    | { text: string; when?: string; unless?: string }
    | { heading: string; when?: string; unless?: string }
    | { list: LegalLetterLine[]; when?: string; unless?: string };

export interface LegalLetterTemplate {
    /** Language of the letter itself (`lang`) - the institution's, not necessarily the page's. */
    language: string;
    /** BCP 47 locale for dates in the letter. */
    dateLocale: string;
    placeDate: string;
    sender: LegalLetterLine[];
    recipient: LegalLetterLine[];
    subject: string;
    body: LegalLetterBlock[];
    closing: string;
    signature: string;
    signatureCaption: string;
    attachmentsHeader: string;
    attachments: LegalLetterLine[];
}

export interface LegalLetterDefinition {
    id: string;
    label: string;
    description: string;
    fields: LegalLetterField[];
    document: LegalLetterTemplate;
}

export type LegalLetterValues = Record<string, string | boolean>;

export type RenderedBlock = { kind: 'paragraph' | 'heading'; text: string } | { kind: 'list'; items: string[] };

export interface RenderedLetter {
    language: string;
    placeDate: string;
    sender: string[];
    recipient: string[];
    subject: string;
    body: RenderedBlock[];
    closing: string;
    signature: string;
    signatureCaption: string;
    attachmentsHeader: string;
    attachments: string[];
}

export const BLANK = '…………………';

const PLACEHOLDER = /\{(\w+)\}/g;

function isoToday(now: Date): string {
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
    return local.toISOString().slice(0, 10);
}

export function initialValues(letter: LegalLetterDefinition, now = new Date()): LegalLetterValues {
    return Object.fromEntries(
        letter.fields.map((field) => {
            if (field.type === 'checkbox') return [field.name, field.default === 'true'];
            if (field.type === 'date' && field.default === 'today') return [field.name, isoToday(now)];
            return [field.name, field.default ?? ''];
        }),
    );
}

export function isFilled(value: string | boolean | undefined): boolean {
    return typeof value === 'boolean' ? value : (value ?? '').trim() !== '';
}

export function missingRequired(letter: LegalLetterDefinition, values: LegalLetterValues): LegalLetterField[] {
    return letter.fields.filter((field) => field.required && !isFilled(values[field.name]));
}

function visible(item: { when?: string; unless?: string }, values: LegalLetterValues): boolean {
    if (item.when !== undefined && !isFilled(values[item.when])) return false;
    if (item.unless !== undefined && isFilled(values[item.unless])) return false;
    return true;
}

export function renderLetter(letter: LegalLetterDefinition, values: LegalLetterValues): RenderedLetter {
    const { document } = letter;
    const types = new Map(letter.fields.map((field) => [field.name, field.type]));
    const dateFormat = new Intl.DateTimeFormat(document.dateLocale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
    });

    const fill = (template: string) =>
        template.replace(PLACEHOLDER, (_, name: string) => {
            const value = values[name];
            if (!isFilled(value) || typeof value === 'boolean') return BLANK;
            const text = value.trim();
            return types.get(name) === 'date' ? dateFormat.format(new Date(`${text}T00:00:00Z`)) : text;
        });

    const lines = (items: LegalLetterLine[]) =>
        items
            .map((item) => (typeof item === 'string' ? { text: item } : item))
            .filter((item) => visible(item, values))
            .map((item) => fill(item.text));

    const body = document.body
        .filter((block) => visible(block, values))
        .map((block): RenderedBlock => {
            if ('list' in block) return { kind: 'list', items: lines(block.list) };
            if ('heading' in block) return { kind: 'heading', text: fill(block.heading) };
            return { kind: 'paragraph', text: fill(block.text) };
        });

    return {
        language: document.language,
        placeDate: fill(document.placeDate),
        sender: lines(document.sender),
        recipient: lines(document.recipient),
        subject: fill(document.subject),
        body,
        closing: document.closing,
        signature: fill(document.signature),
        signatureCaption: document.signatureCaption,
        attachmentsHeader: document.attachmentsHeader,
        attachments: lines(document.attachments),
    };
}

/** Plain text for the clipboard, in the order of a printed letter. */
export function letterToText(letter: RenderedLetter): string {
    const parts: string[] = [letter.sender.join('\n'), letter.placeDate, letter.recipient.join('\n'), letter.subject];
    for (const block of letter.body) {
        parts.push(block.kind === 'list' ? block.items.map((item) => `- ${item}`).join('\n') : block.text);
    }
    parts.push(`${letter.closing}\n\n${letter.signature}`);
    if (letter.attachments.length > 0) {
        parts.push(
            `${letter.attachmentsHeader}\n${letter.attachments.map((item, index) => `${index + 1}. ${item}`).join('\n')}`,
        );
    }
    return parts.filter((part) => part.trim() !== '').join('\n\n') + '\n';
}
